'use client'

import { useId, useState } from 'react'

import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { ContextualProductListAnalytics } from '@/app/lib/analytics/types'
import { useContextualProductListTracking } from '@/app/lib/analytics/useContextualProductListTracking'
import { cx } from '@/app/lib/utils'

import { ProductCard } from './ProductCard'
import type { ProductGridCardData } from './utils/toGridCards'
import styles from './ProductGrid.module.scss'

/** Tarjetas visibles en móvil/tablet antes de pulsar "Ver más". El recorte lo
 * aplica el CSS (`.gridCapped`); aquí solo se decide si hace falta ofrecer
 * ese botón (FR-014). */
const MAX_ITEMS_MOBILE = 4

interface ProductGridProps {
  readonly items: readonly ProductGridCardData[]
  /**
   * Analítica de la rejilla (`view_item_list`/`select_item`), con `analytics.items`
   * alineado con `items`. Opcional: sin ella (tests) no emite nada. Las cards
   * ocultas por el recorte móvil (CSS) NO cruzan el 50% del viewport, así que su
   * impresión solo se cuenta cuando el visitante las ve.
   */
  readonly analytics?: ContextualProductListAnalytics
}

/**
 * Rejilla del módulo «Productos3Columnas»: 2 columnas en todos los anchos
 * (nunca 3, pese al nombre del catálogo — research.md §1). En móvil/tablet
 * solo se ven las 4 primeras tarjetas hasta pulsar "Ver más"; desde
 * escritorio se ven todas (hasta 8) a la vez, sin ningún control (FR-014,
 * FR-015).
 *
 * A diferencia del carril de `ProductosBotonCompra`/`OtrosProductosIzq`, esta
 * rejilla no tiene scroll horizontal ni flechas: es un despliegue vertical
 * puro (research.md §3). Todas las tarjetas se renderizan siempre en el DOM
 * — el recorte a 4 en móvil/tablet es puramente visual (CSS); el botón "Ver
 * más" solo quita esa clase, no vuelve a pedir ni a montar tarjetas.
 *
 * `'use client'` únicamente por el estado del botón "Ver más" — el resto del
 * módulo (`Productos3Columnas.tsx`) es Server Component (Constitución,
 * Principio I).
 */
export function ProductGrid({ items, analytics }: ProductGridProps) {
  const [expanded, setExpanded] = useState(false)
  const gridId = useId()
  const { trackImpression, trackSelect } = useContextualProductListTracking(analytics)

  const hasMore = items.length > MAX_ITEMS_MOBILE
  const isCapped = hasMore && !expanded

  return (
    <>
      <div id={gridId} className={cx(styles.grid, isCapped && styles.gridCapped)}>
        {items.map(({ id, ...card }, index) => {
          const base = analytics?.items[index]
          const cardElement = <ProductCard {...card} />
          return (
            <div key={id} className={styles.item}>
              {base ? (
                <TrackedProductCard
                  index={index}
                  onImpression={() => trackImpression(`${index}:${base.item_id}`, withIndex(base, index))}
                  onSelect={() => trackSelect(withIndex(base, index))}
                >
                  {cardElement}
                </TrackedProductCard>
              ) : (
                cardElement
              )}
            </div>
          )
        })}
      </div>

      {isCapped && (
        <div className={styles.moreWrap}>
          <button
            type="button"
            className={styles.moreBtn}
            aria-expanded={expanded}
            aria-controls={gridId}
            onClick={() => setExpanded(true)}
          >
            Ver más
          </button>
        </div>
      )}
    </>
  )
}
